
<template>
  <div class="font-sans text-[#1D1D1F] bg-[#FBFBFD] overflow-hidden antialiased">
    
    <section class="container mx-auto px-6 pt-32 pb-20 max-w-7xl">
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        <div data-aos="fade-right">
          <span class="text-blue-600 font-bold tracking-[0.2em] text-[11px] uppercase mb-4 block">Opportunités de Carrière</span>
          <h1 class="text-5xl lg:text-7xl font-bold tracking-tighter leading-[1.1] mb-8">
            Pourquoi choisir <br/><span class="bg-gradient-to-r from-blue-600 to-indigo-500 bg-clip-text text-transparent">l'allemand ?</span>
          </h1>
          <p class="text-[21px] text-[#86868B] font-light leading-relaxed mb-10">
            L'Allemagne est le premier partenaire économique d'Europe. Maîtriser sa langue, c'est accéder à des formations rémunérées et une scolarité gratuite.
          </p>
          <div class="flex flex-wrap gap-4">
            <button class="bg-[#1D1D1F] text-white px-8 py-4 rounded-full font-bold hover:bg-black transition-all active:scale-95 shadow-xl">
              Faites votre choix
            </button>
            <button class="bg-[#F5F5F7] text-[#1D1D1F] px-8 py-4 rounded-full font-bold hover:bg-gray-200 transition-all flex items-center gap-2">
              Télécharger le programme <span class="text-xs">↓</span>
            </button>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-4" data-aos="fade-left">
          <div class="bg-blue-600 p-8 rounded-[2.5rem] shadow-xl flex flex-col justify-center text-center text-white">
            <span class="text-4xl font-bold">1200</span>
            <p class="text-[10px] uppercase tracking-widest font-bold opacity-80 mt-2">Apprenants Formés</p>
          </div>
          <div class="bg-white p-8 rounded-[2.5rem] shadow-sm border border-gray-100 flex flex-col justify-center text-center">
            <span class="text-4xl font-bold text-gray-900">100%</span>
            <p class="text-[10px] uppercase tracking-widest font-bold text-gray-400 mt-2">Réussite Examens</p>
          </div>
        </div>
      </div>
    </section>

    <section class="bg-white py-24">
      <div class="container mx-auto px-6 text-center mb-16">
        <h2 class="text-3xl font-bold tracking-tighter">Votre parcours CECRL</h2>
        <p class="text-[#86868B] font-light">De l'initiation à la maîtrise professionnelle.</p>
      </div>
      <div class="container mx-auto px-6 flex flex-wrap justify-center gap-4 max-w-5xl">
        <div v-for="level in ['A1', 'A2', 'B1', 'B2', 'C1', 'C2']" :key="level" 
             class="w-32 h-32 rounded-[2rem] border border-gray-100 flex flex-col items-center justify-center hover:border-blue-500 hover:shadow-xl transition-all duration-500 group cursor-pointer">
          <span class="text-2xl font-black text-gray-300 group-hover:text-blue-600 transition-colors">{{ level }}</span>
          <span class="text-[9px] font-bold uppercase tracking-tighter mt-1 text-gray-400">Disponible</span>
        </div>
      </div>
    </section>

    <section class="bg-[#F5F5F7] py-24">
      <div class="container mx-auto px-6 lg:px-20 grid grid-cols-1 md:grid-cols-3 gap-8">
        <div v-for="(opt, i) in opportunities" :key="i" class="ios-card-white group" data-aos="fade-up" :data-aos-delay="i * 100">
          <div class="text-4xl mb-8 group-hover:scale-110 transition-transform duration-500">{{ opt.emoji }}</div>
          <h3 class="text-xl font-bold mb-4 tracking-tight">{{ opt.title }}</h3>
          <p class="text-[#86868B] text-sm leading-relaxed mb-6 font-light">{{ opt.desc }}</p>
          <div class="mt-auto font-bold text-blue-600 text-xs uppercase tracking-widest flex items-center gap-2">
            En savoir plus <span>→</span>
          </div>
        </div>
      </div>
    </section>

    <section class="py-24 bg-white">
      <div class="container mx-auto px-6 text-center mb-16">
        <h3 class="text-[11px] uppercase tracking-[0.3em] font-bold text-gray-400 mb-4">Ils nous font confiance</h3>
        <h2 class="text-3xl font-bold tracking-tighter">Partenaires & Écoles de Référence</h2>
      </div>
      <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-px bg-gray-100 border-y border-gray-100 max-w-7xl mx-auto">
        <div v-for="(partner, index) in partners" :key="index" class="bg-white p-12 flex items-center justify-center group">
          <img :src="partner.logo" class="h-10 object-contain grayscale opacity-40 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-700" :alt="partner.name"/>
        </div>
      </div>
    </section>

    <section class="py-24 bg-[#FBFBFD]">
      <div class="container mx-auto px-6 lg:px-20">
        <div class="flex flex-col md:flex-row justify-between items-end mb-12">
          <div class="max-w-xl">
            <h2 class="text-4xl font-bold tracking-tighter mb-4">Témoignages Vérifiés</h2>
            <p class="text-[#86868B] font-light">Découvrez l'impact de NovaLingua sur la carrière de nos anciens apprenants.</p>
          </div>
          <div class="mt-6 md:mt-0 bg-white px-6 py-3 rounded-full shadow-sm border border-gray-100 flex items-center gap-4">
            <span class="text-2xl font-bold">4.9/5</span>
            <div class="text-yellow-400">★★★★★</div>
          </div>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div v-for="(testi, i) in testimonials" :key="i" class="bg-white p-10 rounded-[2.5rem] shadow-sm border border-gray-100 hover:shadow-xl transition-shadow duration-500">
            <p class="text-[#1D1D1F] text-lg leading-relaxed mb-8 font-light italic">"{{ testi.text }}"</p>
            <div class="flex items-center gap-4 pt-6 border-t border-gray-50">
              <div class="w-10 h-10 bg-[#F5F5F7] rounded-full flex items-center justify-center font-bold text-xs">{{ testi.initials }}</div>
              <div>
                <p class="font-bold text-sm">{{ testi.name }}</p>
                <p class="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Publié le {{ testi.date }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="pb-24 px-6">
      <div class="max-w-5xl mx-auto bg-[#1D1D1F] rounded-[3.5rem] p-16 text-center text-white relative overflow-hidden group">
        <h2 class="text-4xl md:text-5xl font-bold mb-6 tracking-tight">Boostez votre carrière.</h2>
        <p class="text-gray-400 mb-10 text-lg font-light max-w-xl mx-auto">Rejoignez nos sessions intensives et préparez votre départ pour l'Allemagne ou le reste du monde.</p>
        <button class="bg-white text-black px-12 py-5 rounded-full font-bold hover:scale-105 active:scale-95 transition-all shadow-2xl">
          S'inscrire Maintenant
        </button>
        <div class="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-blue-600/10 via-transparent to-indigo-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-1000 pointer-events-none"></div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref } from 'vue';

const opportunities = [
  { emoji: '💰', title: 'Formations Rémunérées', desc: 'Soins infirmiers, informatique, BTP. Salaire dès le premier mois de formation avec un Baccalauréat.' },
  { emoji: '🚀', title: 'Études Universitaires', desc: 'Accédez au Bachelor ou Master en Allemagne avec une scolarité totalement gratuite.' },
  { emoji: '🌍', title: 'Carrière Internationale', desc: 'Mobilité vers l\'Allemagne, l\'Autriche et la Suisse avec des conditions de travail d\'élite.' }
];

const partners = ref([
  {
    name: "IHK (Industrie- und Handelskammer)",
    type: "Chambre de Commerce et d'Industrie",
    description: "La référence nationale pour les formations commerciales, industrielles et techniques en Allemagne.",
    logo: require('@/assets/images/ihk.png'),
    link: "https://www.ihk.de"
  },
  {
    name: "HWK (Handwerkskammer)",
    type: "Chambre des Métiers",
    description: "Organisme leader pour les métiers artisanaux et l'obtention du titre prestigieux de Meister.",
    logo: require('@/assets/images/zdh.png'),
    link: "https://www.zdh.de"
  },
 {
    name: "Berufsschule",
    type: "École Professionnelle d'État",
    description: "Pilier du système dual allemand, combinant apprentissage en entreprise et formation théorique.",
    logo: "https://www.einstieg.com/typo3conf/ext/twt_templates_frontend/Resources/Public/img/einstieg-logo-small.svg",
    link: "https://www.make-it-in-germany.com/fr/formation-travail/formation-professionnelle/le-systeme-dual"
  },
  {
    name: "Fachschule",
    type: "École Technique Supérieure",
    description: "Formations techniques avancées pour devenir Technicien certifié d'État ou Économiste d'entreprise.",
    logo: require('@/assets/images/europaschule.png'),
    link: "https://www.kmk.org/themen/berufliche-schulen/berufliche-fortbildung/fachschulen.html"
  },
  {
    name: "Berufsfachschule",
    type: "École de Formation Continue",
    description: "Spécialisée dans les domaines de la santé, du social, de l'informatique et du commerce à temps plein.",
    logo: require('@/assets/images/bringt.png'),
    link: "https://www.arbeitsagentur.de/bildung/ausbildung/berufsfachschule-ausbildung"
  },
  {
    name: "Berufliches Gymnasium",
    type: "Lycée Professionnel",
    description: "Parcours d'élite combinant formation technique spécialisée et obtention de l'Abitur allemand.",
    logo: require('@/assets/images/konferenz.png'),
    link: "https://www.kmk.org/themen/berufliche-schulen/berufsqualifizierende-bildung-an-beruflichen-schulen/berufliches-gymnasium.html"
  },
  {
    name: "DHBW (Duale Hochschule)",
    type: "Université Duale",
    description: "Le modèle pionnier d'études intégrées entre théorie académique et pratique intensive en entreprise.",
    logo: require('@/assets/images/dhbw.png'),
    link: "https://www.dhbw.de"
  },
  {
    name: "TÜV Akademie",
    type: "Académie de Certification",
    description: "Leader mondial de la certification technique, qualité et sécurité industrielle en Allemagne.",
    logo: require('@/assets/images/tuvnord.png'),
    link: "https://www.tuev-nord.de"
  },
  {
    name: "DEKRA Akademie",
    type: "Formation & Reconversion",
    description: "L'un des plus grands centres de formation continue en Europe, spécialisé en logistique et IT.",
    logo: require('@/assets/images/dekra_100.png'),
    link: "https://www.dekra-akademie.de"
  },
  {
    name: "Institut Goethe",
    type: "Certification Allemand",
    description: "Le partenaire mondial pour la validation de vos compétences linguistiques de A1 à C2.",
    logo: require('@/assets/images/goethe.png'),
    link: "https://www.goethe.de"
  },
  {
    name: "British Council",
    type: "Certification Anglais",
    description: "Expert international pour les tests IELTS requis pour les programmes anglophones en Allemagne.",
    logo: require('@/assets/images/british.png'),
    link: "https://www.britishcouncil.org"
  }
]);
const testimonials = ref([
  { name: "Naomi Moupe", initials: "NM", date: "08/12/2025", text: "Super centre linguistique, je recommande vivement. L'accompagnement pour le visa a été crucial pour ma réussite." },
  { name: "Talla KTBM", initials: "TK", date: "24/11/2025", text: "Le meilleur centre à Bafoussam. La formation en allemand est d'un niveau supérieur, j'ai eu mon B2 du premier coup." }
]);
</script>

<style scoped>
/* Police Système Apple */
.font-sans {
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display", "Helvetica Neue", Arial, sans-serif;
  letter-spacing: -0.015em;
}

/* Apple Card Style */
.ios-card-white {
  @apply bg-white p-12 rounded-[2.8rem] shadow-sm border border-gray-50 flex flex-col transition-all duration-700 hover:shadow-[0_40px_80px_rgba(0,0,0,0.05)] hover:-translate-y-2;
}

.tracking-tighter {
  letter-spacing: -0.03em;
}

/* Désactivation grayscale logos sur mobile pour visibilité */
@media (max-width: 768px) {
  .grayscale { filter: none !important; opacity: 1 !important; }
}
</style>